import { useState, useRef, useEffect, useCallback } from 'react';
import { Clipboard, ChevronDown, Check, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClipboardPresets } from '../../hooks/useClipboardPresets';
import { useColorContext } from '../../contexts/ColorContext';

export function ClipboardDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [justCopied, setJustCopied] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { result } = useColorContext();
  const { presets, loading, getDefaultPreset, applyPreset } = useClipboardPresets();

  const hex = result?.hex ?? '';

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const copyToClipboard = useCallback(async (text: string, presetId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setJustCopied(presetId);
      setTimeout(() => setJustCopied(null), 1500);
    } catch {
      console.error('Clipboard write failed');
    }
  }, []);

  const handleDefaultCopy = useCallback(() => {
    if (!hex) return;
    const defaultPreset = getDefaultPreset();
    if (!defaultPreset?.id) {
      void navigator.clipboard.writeText(hex);
      return;
    }
    const output = applyPreset(defaultPreset.id, hex);
    void copyToClipboard(output, 'default');
  }, [hex, getDefaultPreset, applyPreset, copyToClipboard]);

  const handlePresetCopy = useCallback((presetId: string) => {
    if (!hex) return;
    const output = applyPreset(presetId, hex);
    void copyToClipboard(output, presetId);
  }, [hex, applyPreset, copyToClipboard]);

  if (loading || !hex) return null;

  const defaultPreset = getDefaultPreset();
  const defaultLabel = defaultPreset?.name ?? 'Copy';

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Split button: copy default + dropdown trigger */}
      <div style={{
        display: 'flex', alignItems: 'stretch',
        borderRadius: 'var(--radius, 6px)',
        border: '1px solid var(--line, #e5e7eb)',
        overflow: 'hidden',
      }}>
        {/* Main copy button */}
        <button
          type="button"
          onClick={handleDefaultCopy}
          title={`Copy as ${defaultLabel}`}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', fontSize: '12px', fontWeight: 500,
            background: justCopied === 'default' ? 'var(--good-bg, oklch(0.95 0.05 145))' : 'var(--panel, #fff)',
            color: justCopied === 'default' ? 'var(--good, oklch(0.45 0.15 145))' : 'var(--ink, #111)',
            border: 'none', cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
          }}
        >
          {justCopied === 'default' ? <Check size={13} /> : <Clipboard size={13} />}
          {justCopied === 'default' ? 'Copied' : defaultLabel}
        </button>

        {/* Dropdown trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          title="Copy as…"
          style={{
            display: 'flex', alignItems: 'center',
            padding: '4px 6px',
            background: isOpen ? 'var(--panel-2, #f3f4f6)' : 'var(--panel, #fff)',
            borderLeft: '1px solid var(--line, #e5e7eb)',
            border: 'none', borderLeftStyle: 'solid', borderLeftWidth: '1px',
            borderLeftColor: 'var(--line, #e5e7eb)',
            cursor: 'pointer', color: 'var(--ink-3, #999)',
          }}
        >
          <ChevronDown size={12} />
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          minWidth: '260px', maxHeight: '320px', overflowY: 'auto',
          background: 'var(--panel, #fff)',
          border: '1px solid var(--line, #e5e7eb)',
          borderRadius: 'var(--radius-lg, 8px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)',
          zIndex: 100,
          padding: '4px',
        }}>
          <div style={{
            padding: '8px 10px 6px',
            fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'var(--ink-3, #999)',
          }}>
            Copy as…
          </div>
          {presets.map((preset) => {
            const output = applyPreset(preset.id!, hex);
            const isCopied = justCopied === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePresetCopy(preset.id!)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: '2px',
                  width: '100%', textAlign: 'left',
                  padding: '8px 10px', borderRadius: 'var(--radius, 6px)',
                  border: 'none', cursor: 'pointer',
                  background: isCopied ? 'var(--good-bg, oklch(0.95 0.05 145))' : 'transparent',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { if (!isCopied) e.currentTarget.style.background = 'var(--panel-2, #f9fafb)'; }}
                onMouseLeave={(e) => { if (!isCopied) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  fontSize: '13px', fontWeight: 500,
                  color: isCopied ? 'var(--good, oklch(0.45 0.15 145))' : 'var(--ink, #111)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  {isCopied && <Check size={12} />}
                  {preset.name}
                  {preset.isDefault && (
                    <span style={{
                      fontSize: '10px', padding: '1px 5px',
                      borderRadius: '3px',
                      background: 'var(--accent-bg, oklch(0.95 0.03 260))',
                      color: 'var(--accent, oklch(0.55 0.15 260))',
                      fontWeight: 600,
                    }}>
                      DEFAULT
                    </span>
                  )}
                </span>
                <span style={{
                  fontSize: '11px', fontFamily: 'monospace',
                  color: 'var(--ink-3, #999)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isCopied ? 'Copied ✓' : output}
                </span>
              </button>
            );
          })}
          <div style={{
            borderTop: '1px solid var(--line, #e5e7eb)',
            marginTop: '4px', paddingTop: '4px',
          }}>
            <button
              type="button"
              onClick={() => { navigate('/clipboard'); setIsOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                width: '100%', padding: '8px 10px',
                borderRadius: 'var(--radius, 6px)',
                border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: '12px',
                color: 'var(--ink-2, #6b7280)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--panel-2, #f9fafb)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Settings size={12} />
              Manage presets…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
